import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from './Button'
import { DOCK, sameSpot, toastSpot, type ToastSpot } from '../lib/toastSpot'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastSpec {
  text: string
  /** Almost always Undo. D6 and F7 both rest on this: removal is a swipe and a
   *  toast, not a confirmation dialog. */
  action?: ToastAction
  ms?: number
}

const DEFAULT_MS = 5000

interface Live extends ToastSpec { id: number }

/**
 * One toast, in module state.
 *
 * Not a context with a provider, because `useToast` is called from mutation
 * callbacks and event handlers all over the app and a provider would mean every
 * one of them had to be inside a tree the host also wraps. A module-level
 * store has one subscriber — the single `<Toast />` in `App.tsx` — and callers
 * anywhere.
 *
 * "One at a time" is not a queue with a limit of one: it is a single slot. A
 * second toast replaces the first, which is right for the thing this is for —
 * the undo that matters is the one for what you just did, and a queue would
 * make you wait five seconds to be offered it.
 */
let current: Live | null = null
let nextId = 0
const listeners = new Set<(t: Live | null) => void>()

function publish(next: Live | null) {
  current = next
  listeners.forEach(fn => fn(next))
}

/** Show a toast. Returns nothing: a caller that wants to know what happened to
 *  it is describing a dialog, not a toast. */
export function toast(spec: ToastSpec) {
  publish({ ...spec, id: nextId++ })
}

/** The same function, reached the way a component reaches things. Stable, so it
 *  is safe in a dependency array. */
export function useToast(): (spec: ToastSpec) => void {
  return toast
}

/** The sheet on top, if one is open: the last `.u-sheet` in the document
 *  (each opens in its own portal, appended in the order they opened) that is
 *  drawn at all — a `chromeHidden` sheet is open but invisible under the one
 *  stacked on it (`ui/Sheet`). */
function topSheet(): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>('.u-sheet')
  for (let i = all.length - 1; i >= 0; i--) {
    const el = all[i]
    if (el.style.opacity === '0') continue
    const r = el.getBoundingClientRect()
    if (r.height > 0 && r.top < window.innerHeight) return el
  }
  return null
}

/**
 * The host. Mounted once, at the root, outside the scrolling shell.
 *
 * `aria-live="polite"` on a wrapper that is always in the DOM, rather than on
 * the toast itself: a live region has to exist before its content changes or
 * the change is not announced.
 */
export function Toast({ className = '' }: { className?: string }) {
  const [live, setLive] = useState<Live | null>(current)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const host = useRef<HTMLDivElement>(null)
  // UI-audit a1-14: clear of any open sheet (`lib/toastSpot.ts`). Followed
  // every frame while a toast is up — a sheet opens, is dragged and closes
  // under a live toast, and five seconds of one rect read a frame is cheap.
  // A layout effect, so the first frame is already placed: drawn docked for
  // one paint it would flash over the very control this is for.
  const [spot, setSpot] = useState<ToastSpot>(DOCK)
  useLayoutEffect(() => {
    if (!live) { setSpot(DOCK); return }
    let raf = 0
    const tick = () => {
      const sheet = topSheet()
      const h = host.current?.firstElementChild?.getBoundingClientRect().height || 52
      const next = toastSpot(sheet ? sheet.getBoundingClientRect().top : null, h)
      setSpot(prev => (sameSpot(prev, next) ? prev : next))
      raf = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(raf)
  }, [live])
  const held = spot.mode === 'held'
  // What is left of this toast's time. A held toast's clock stops, so it
  // gets the rest of its five seconds once it can be seen.
  const left = useRef(DEFAULT_MS)

  useEffect(() => {
    listeners.add(setLive)
    return () => { listeners.delete(setLive) }
  }, [])

  useEffect(() => { if (live) left.current = live.ms ?? DEFAULT_MS }, [live])
  useEffect(() => {
    if (timer.current !== null) { clearTimeout(timer.current); timer.current = null }
    if (!live || held) return
    const id = live.id
    const started = Date.now()
    timer.current = setTimeout(() => {
      timer.current = null
      // Only if it is still this one. A toast that arrived after this timer was
      // set has its own life and must not be cut short by the previous one's —
      // the bug this codebase keeps writing, in a setTimeout.
      if (current?.id === id) publish(null)
    }, left.current)
    return () => {
      if (timer.current !== null) clearTimeout(timer.current)
      left.current = Math.max(0, left.current - (Date.now() - started))
    }
  }, [live, held])

  return (
    // A press on a toast is never a press outside anything else. An open
    // sheet's dismiss-on-outside (Radix, under Vaul) listens on `document`
    // and, for a mouse or finger, waits for the click — by which time Undo
    // has already removed the toast, so the press reads as outside and the
    // sheet the Undo was answering shut with it (a2-35: a note deleted in
    // Account, put back by Undo into a sheet that had just closed). Stopped
    // here, the press never reaches `document`.
    <div
      ref={host}
      className={`u-toast-host ${held ? 'is-held' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
      style={spot.mode === 'above' ? { top: spot.top, bottom: 'auto' } : undefined}
      aria-live="polite" aria-atomic="true"
      onPointerDown={e => e.stopPropagation()}
    >
      {live && (
        <div className="u-toast" key={live.id}>
          <span className="u-toast__text">{live.text}</span>
          {live.action && (
            <Button
              variant="quiet"
              size="sm"
              className="u-toast__action"
              onClick={() => { live.action?.onClick(); publish(null) }}
            >
              {live.action.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
